import { exhaustive } from "exhaustive"
import { FC, useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { z } from "zod"
import { AbsolutCentered } from "../../AbsolutCentered/AbsolutCentered"
import { useAuth } from "../../Auth/AuthContext"
import { useClient } from "../../Client/ClientAndUserProvider"
import { GoogleTagManagerMode } from "../../Client/FeatureTypes"
import { CoredinationMode } from "../../Client/GetClientResponse"
import { PenIcon } from "../../Icons/Icon"
import { Loader } from "../../Loader/Loader"
import { PermissionAreaLocation, usePermissions } from "../../PermissionContext"
import { EventQueue } from "../../Shared/eventQueue"
import { TabbedSubView, TabbedSubViewMenuItem } from "../../Shared/TabbedSubView/TabbedSubView"
import { useBrandedLocalStorage } from "../../Shared/useBrandedLocalStorage"
import { CustomerPortalClientOrderEmailReceivers } from "../CustomerPortalClientOrderEmailReceivers/CustomerPortalClientOrderEmailReceivers"
import { CustomerPortalConsumerDetails } from "../CustomerPortalConsumerDetails/CustomerPortalConsumerDetails"
import { CustomerPortalConsumerGroups } from "../CustomerPortalConsumerGroups/CustomerPortalConsumerGroups"
import consumerInfoStyle from "../CustomerPortalConsumerInformation/CustomerPortalConsumerInformation.module.css"
import { CustomerPortalConsumerTags } from "../CustomerPortalConsumerTags/CustomerPortalConsumerTags"
import myAccountStyle from "../CustomerPortalMyAccount/CustomerPortalMyAccount.module.css"
import { EditClientContactDetails } from "../EditClientContactDetails/EditClientContactDetails"
import { CoredinationSettings } from "../ExternalIntegrations/Coredination/CoredinationSettings/CoredinationSettings"
import { GoogleTagManagerSettings } from "../GoogleTagManagerSettings/GoogleTagManagerSettings"
import { KlarnaSettings } from "../KlarnaSettings/KlarnaSettings"
import { Users } from "../Users/Users"
import style from "./CustomerPortalCustomerDetails.module.css"

function onWheel(event: globalThis.WheelEvent) {
	const target = event.target as HTMLElement
	if (target && target.clientWidth < target.scrollWidth) {
		event.preventDefault()
	}
	;(event.target as HTMLElement).scrollLeft += event.deltaY
}

export function onWheelRef(node: HTMLElement | null) {
	if (node) {
		node.addEventListener("wheel", (event) => onWheel(event), { passive: false })
	}
}

const ClientDetails: FC = () => {
	const client = useClient()
	return (
		<div className={consumerInfoStyle.wrapper}>
			<div className={consumerInfoStyle.headerText} style={{ marginBottom: "35px" }}>
				Mitt företag
			</div>

			<div className={consumerInfoStyle.textInputWithLabel}>
				<label>Företagsnamn</label>
				<span ref={onWheelRef} title={client.clientInfo.clientName} className={consumerInfoStyle.textAsInput}>
					{client.clientInfo.clientName}
				</span>
			</div>
			<div className={consumerInfoStyle.textInputWithLabel}>
				<label>Organisationsnummer</label>
				<span
					ref={onWheelRef}
					title={client.clientInfo.organizationNumber}
					className={consumerInfoStyle.textAsInput}>
					{client.clientInfo.organizationNumber}
				</span>
			</div>
		</div>
	)
}

const ContactDetails: FC = () => {
	const client = useClient()
	const permissions = usePermissions()
	const auth = useAuth()

	const [edit, setEdit] = useState(false)

	if (!permissions.isAllowed(PermissionAreaLocation.Client_Information_View) || !auth.IsLoggedInClient) {
		return null
	}

	return (
		<>
			{edit && permissions.isAllowed(PermissionAreaLocation.Client_Information_Update) ? (
				<EditClientContactDetails
					onClose={() => {
						setEdit(false)
					}}
					onDone={() => {
						setEdit(false)
						EventQueue.dispatchEvent("reload-client-data", {})
					}}
				/>
			) : null}
			<div style={{ display: "flex", width: "100%", columnGap: "22px", rowGap: "15px", flexWrap: "wrap" }}>
				<div className={consumerInfoStyle.wrapper}>
					<div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
						<div className={consumerInfoStyle.headerText}>Kontaktdetaljer</div>
						{permissions.isAllowed(PermissionAreaLocation.Client_Information_Update) ? (
							<div>
								<PenIcon
									size={22}
									iconClassName={myAccountStyle.editIcon}
									onClick={() => {
										setEdit(true)
									}}
								/>
							</div>
						) : null}
					</div>
					<div className={consumerInfoStyle.textInputWithLabel} style={{ marginTop: "35px" }}>
						<label>Visningsnamn</label>
						<span
							ref={onWheelRef}
							className={consumerInfoStyle.textAsInput}
							title={client.clientInfo.contact.name}>
							{client.clientInfo.contact.name}
						</span>
					</div>
					<div className={consumerInfoStyle.textInputWithLabel}>
						<label>Hemsida</label>
						<span ref={onWheelRef} className={consumerInfoStyle.textAsInput} title={client.clientInfo.homepage}>
							{client.clientInfo.homepage}
						</span>
					</div>
					<div className={consumerInfoStyle.textInputWithLabel}>
						<label>E-post för ordermottagning</label>
						<span
							ref={onWheelRef}
							className={consumerInfoStyle.textAsInput}
							title={client.clientInfo.contact.orderEmail}>
							{client.clientInfo.contact.orderEmail}
						</span>
					</div>
					<div className={consumerInfoStyle.twoInputsSameRow}>
						<div className={consumerInfoStyle.textInputWithLabel}>
							<label>Telefonnummer</label>
							<span
								ref={onWheelRef}
								className={consumerInfoStyle.textAsInput}
								title={client.clientInfo.contact.phoneNumber}>
								{client.clientInfo.contact.phoneNumber}
							</span>
						</div>
						<div></div>
						<div className={consumerInfoStyle.textInputWithLabel}>
							<label>E-post</label>
							<span
								ref={onWheelRef}
								className={consumerInfoStyle.textAsInput}
								title={client.clientInfo.contact.email}>
								{client.clientInfo.contact.email}
							</span>
						</div>
					</div>
					<div className={consumerInfoStyle.textInputWithLabel}>
						<label>Besöksadress</label>
						<span
							ref={onWheelRef}
							className={consumerInfoStyle.textAsInput}
							title={client.clientInfo?.address?.street}>
							{client.clientInfo?.address?.street}
						</span>
					</div>
					<div className={consumerInfoStyle.twoInputsSameRow}>
						<div className={consumerInfoStyle.textInputWithLabel}>
							<label>Postnummer</label>
							<span
								ref={onWheelRef}
								className={consumerInfoStyle.textAsInput}
								title={client.clientInfo?.address?.zipCode}>
								{client.clientInfo?.address?.zipCode}
							</span>
						</div>
						<div></div>
						<div className={consumerInfoStyle.textInputWithLabel}>
							<label>Postort</label>
							<span
								ref={onWheelRef}
								className={consumerInfoStyle.textAsInput}
								title={client.clientInfo?.address?.city}>
								{client.clientInfo?.address?.city}
							</span>
						</div>
					</div>
				</div>
				<CustomerPortalClientOrderEmailReceivers key={"clientDetailsOrderReceiverEmails"} />
			</div>
		</>
	)
}

enum ClientTabs {
	ClientDetails = "ClientDetails",
	Payment = "Payment",
	ExternalIntegrations = "ExternalIntegrations",
	Users = "Users",
	Miscellaneous = "Miscellaneous",
}

const CustomerPortalClientDetails: FC = () => {
	const auth = useAuth()
	const client = useClient()
	const permissions = usePermissions()

	const [queryParams, setQueryParams] = useSearchParams()

	const menuItems = useMemo<TabbedSubViewMenuItem[]>(() => {
		return [
			{
				id: ClientTabs.ClientDetails,
				name: "Översikt",
				onClick: () => {},
				disabled: false,
				hidden: false,
				queryParamIdValue: null,
			},
			{
				id: ClientTabs.Payment,
				name: "Betalning",
				onClick: () => {},
				disabled: false,
				hidden: !client.features.allowKlarnaIntegration,
				queryParamIdValue: null,
			},
			{
				id: ClientTabs.Users,
				name: "Användare",
				onClick: () => {},
				disabled: false,
				hidden: !permissions.isAllowed(PermissionAreaLocation.Client_Users_Read),
				queryParamIdValue: null,
			},
			{
				id: ClientTabs.ExternalIntegrations,
				name: "Externa integreringar",
				onClick: () => {},
				disabled: false,
				hidden:
					client.externalIntegrationSettings.coredination.mode === CoredinationMode.Disabled &&
					client.features.googleTagManagerMode === GoogleTagManagerMode.Disabled,
				queryParamIdValue: null,
			},
			{
				id: ClientTabs.Miscellaneous,
				name: "Övrigt",
				onClick: () => {},
				disabled: false,
				hidden:
					!permissions.isAllowed(PermissionAreaLocation.Consumer_Information_View) &&
					!permissions.isAllowed(PermissionAreaLocation.Client_Consumer_Groups_Read),
				queryParamIdValue: null,
			},
		]
	}, [client, permissions])

	const currentSelectedTab: ClientTabs = !!queryParams.get("tab")
		? (queryParams.get("tab") as ClientTabs)
		: ClientTabs.ClientDetails

	useEffect(() => {
		if (
			!queryParams.get("tab") ||
			(!!queryParams.get("tab") && !Object.values(ClientTabs).includes(queryParams.get("tab") as any))
		) {
			queryParams.set("tab", ClientTabs.ClientDetails)
			setQueryParams(queryParams, { replace: true })
		}
	}, [])

	if (!auth.IsLoggedInClient) {
		return null
	}

	function canViewTab(tab: ClientTabs): boolean {
		return exhaustive(tab, {
			[ClientTabs.ClientDetails]: () => {
				return true
			},
			[ClientTabs.Payment]: () => {
				return client.features.allowKlarnaIntegration
			},
			[ClientTabs.ExternalIntegrations]: () => {
				return (
					client.externalIntegrationSettings.coredination.mode !== CoredinationMode.Disabled ||
					client.features.googleTagManagerMode !== GoogleTagManagerMode.Disabled
				)
			},
			[ClientTabs.Users]: () => {
				return permissions.isAllowed(PermissionAreaLocation.Client_Users_Read)
			},
			[ClientTabs.Miscellaneous]: () => {
				return (
					permissions.isAllowed(PermissionAreaLocation.Consumer_Information_View) ||
					permissions.isAllowed(PermissionAreaLocation.Client_Consumer_Groups_Read)
				)
			},
			_: () => null,
		})
	}

	function tabContent() {
		if (!canViewTab(currentSelectedTab)) {
			return null
		}

		return exhaustive(currentSelectedTab, {
			[ClientTabs.ClientDetails]: () => {
				return [<ClientDetails key={"clientDetails"} />, <ContactDetails key={"contactDetails"} />]
			},
			[ClientTabs.Payment]: () => {
				return <KlarnaSettings />
			},
			[ClientTabs.ExternalIntegrations]: () => {
				return (
					<>
						{client.externalIntegrationSettings.coredination.mode !== CoredinationMode.Disabled ? (
							<CoredinationSettings />
						) : null}

						{client.features.googleTagManagerMode !== GoogleTagManagerMode.Disabled ? (
							<GoogleTagManagerSettings />
						) : null}
					</>
				)
			},
			[ClientTabs.Users]: () => {
				return (
					<span style={{ display: "block", maxWidth: "600px", width: "100%" }}>
						<Users type={"Client"} clientIdentifier={client.identifier} />
					</span>
				)
			},
			[ClientTabs.Miscellaneous]: () => {
				return (
					<>
						{permissions.isAllowed(PermissionAreaLocation.Consumer_Information_View) ? (
							<CustomerPortalConsumerTags />
						) : null}

						{permissions.isAllowed(PermissionAreaLocation.Client_Consumer_Groups_Read) &&
						client.features.allowSettingConsumerGroupConnections ? (
							<CustomerPortalConsumerGroups />
						) : null}
					</>
				)
			},
			_: () => null,
		})
	}

	return (
		<TabbedSubView
			profilePicture={null}
			name={client.clientInfo.clientName}
			infoBoxes={[]}
			menuItems={menuItems}
			onBeforeQueryParamSet={(params) => {
				if (params.has("showEvents") || params.has("page") || params.has("pageSize")) {
					params.delete("showEvents")
					params.delete("page")
					params.delete("pageSize")
				}
				return params
			}}
			cancelAction={null}>
			<div className={style.clientDetailsWrapper}>{tabContent()}</div>
		</TabbedSubView>
	)
}

export const CustomerPortalCustomerDetails: FC = () => {
	const auth = useAuth()

	const [selectedConsumerId] = useBrandedLocalStorage("selected-consumer-id", z.string(), {
		defaultValue: "",
	})

	if (!auth.Me) {
		return (
			<AbsolutCentered>
				<Loader />
			</AbsolutCentered>
		)
	}

	return (
		<>
			{auth.Me.type === "Consumer" ? (
				<CustomerPortalConsumerDetails consumerId={selectedConsumerId} />
			) : (
				<CustomerPortalClientDetails />
			)}
		</>
	)
}
